import React, { useState, useEffect } from 'react';
import { authAPI } from '../../../api/auth';
import './settings.css';

const Settings = () => {
  const [mexcApiKey, setMexcApiKey] = useState('');
  const [mexcApiSecret, setMexcApiSecret] = useState('');
  const [mexcActive, setMexcActive] = useState(false);
  const [binanceApiKey, setBinanceApiKey] = useState('');
  const [binanceApiSecret, setBinanceApiSecret] = useState('');
  const [binanceActive, setBinanceActive] = useState(false);
  const [sideMinAmt, setSideMinAmt] = useState('');
  const [maxAmt, setMaxAmt] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      const response = await authAPI.getApiKeys();
      
      if (response.success && response.data) {
        const data = response.data;
        if (data.mexc_api_key) {
          setMexcApiKey(data.mexc_api_key || '');
        }
        if (data.mexc_api_secret) {
          setMexcApiSecret(data.mexc_api_secret || '');
        }
        setMexcActive(data.mexc_active === true || data.mexc_active === 1);
        if (data.binance_api_key) {
          setBinanceApiKey(data.binance_api_key || '');
        }
        if (data.binance_api_secret) {
          setBinanceApiSecret(data.binance_api_secret || '');
        }
        setBinanceActive(data.binance_active === true || data.binance_active === 1);
        if (data.side_min_amt) {
          setSideMinAmt(data.side_min_amt || '');
        }
        if (data.max_amt) {
          setMaxAmt(data.max_amt || '');
        }
        if (data.min_price) {
          setMinPrice(data.min_price || '');
        }
        if (data.max_price) {
          setMaxPrice(data.max_price || '');
        }
      }
    } catch (error) {
      console.error('Error loading API keys:', error);
    }
  };

  const saveApiKeys = async () => {
    setLoading(true);
    setMessage('');
    
    try {
      const response = await authAPI.saveApiKeys({
        mexc_api_key: mexcApiKey,
        mexc_api_secret: mexcApiSecret,
        mexc_active: mexcActive,
        binance_api_key: binanceApiKey,
        binance_api_secret: binanceApiSecret,
        binance_active: binanceActive,
        side_min_amt: sideMinAmt,
        max_amt: maxAmt,
        min_price: minPrice,
        max_price: maxPrice
      });
      
      let message = 'API keys saved successfully!';
      
      // Check validation results
      if (response.validation) {
        const validationMessages = [];
        
        if (response.validation.mexc) {
          if (response.validation.mexc.valid) {
            validationMessages.push('✅ MEXC API credentials are valid');
          } else {
            validationMessages.push(`❌ MEXC API credentials are invalid: ${response.validation.mexc.message}`);
          }
        }
        
        if (response.validation.binance) {
          if (response.validation.binance.valid) {
            validationMessages.push('✅ Binance API credentials are valid');
          } else {
            validationMessages.push(`❌ Binance API credentials are invalid: ${response.validation.binance.message}`);
          }
        }
        
        if (validationMessages.length > 0) {
          message += '\n\n' + validationMessages.join('\n');
        }
      }
      
      setMessage(message);
      setMessageType('success');
    } catch (error) {
      let errorMessage = 'Error saving API keys. Please try again.';
      
      // Handle validation errors from backend
      if (error.response?.data?.validation) {
        const validationMessages = [];
        
        if (error.response.data.validation.mexc && !error.response.data.validation.mexc.valid) {
          validationMessages.push(`❌ MEXC API credentials are invalid: ${error.response.data.validation.mexc.message}`);
        }
        
        if (error.response.data.validation.binance && !error.response.data.validation.binance.valid) {
          validationMessages.push(`❌ Binance API credentials are invalid: ${error.response.data.validation.binance.message}`);
        }
        
        if (validationMessages.length > 0) {
          errorMessage = validationMessages.join('\n');
        }
      }
      
      setMessage(errorMessage);
      setMessageType('error');
      console.error('Error saving API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-container">
      <div className="settings-card">
        <h3 className="card-title">Exchange API Keys</h3>
        
        <div className="exchange-section mexc-section">
          <h4 className="exchange-title">
            <div className="exchange-icon mexc">M</div>
            Mexc Exchange
          </h4>
          <div className="form-group">
            <div className="checkbox-wrapper">
              <input
                id="mexc-active"
                type="checkbox"
                className="checkbox-input"
                checked={mexcActive}
                onChange={(e) => setMexcActive(e.target.checked)}
              />
              <label htmlFor="mexc-active" className="checkbox-label">Active</label>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="mexc-api-key" className="form-label">API Key</label>
            <input
              id="mexc-api-key"
              type="text"
              className="form-input"
              value={mexcApiKey}
              onChange={(e) => setMexcApiKey(e.target.value)}
              placeholder="Enter your Mexc API key"
            />
          </div>
          <div className="form-group">
            <label htmlFor="mexc-api-secret" className="form-label">API Secret</label>
            <input
              id="mexc-api-secret"
              type="password"
              className="form-input"
              value={mexcApiSecret}
              onChange={(e) => setMexcApiSecret(e.target.value)}
              placeholder="Enter your Mexc API secret"
            />
          </div>
        </div>

        <div className="exchange-section binance-section">
          <h4 className="exchange-title">
            <div className="exchange-icon binance">B</div>
            Binance Exchange
          </h4>
          <div className="form-group">
            <div className="checkbox-wrapper">
              <input
                id="binance-active"
                type="checkbox"
                className="checkbox-input"
                checked={binanceActive}
                onChange={(e) => setBinanceActive(e.target.checked)}
              />
              <label htmlFor="binance-active" className="checkbox-label">Active</label>
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="binance-api-key" className="form-label">API Key</label>
            <input
              id="binance-api-key"
              type="text"
              className="form-input"
              value={binanceApiKey}
              onChange={(e) => setBinanceApiKey(e.target.value)}
              placeholder="Enter your Binance API key"
            />
          </div>
          <div className="form-group">
            <label htmlFor="binance-api-secret" className="form-label">API Secret</label>
            <input
              id="binance-api-secret"
              type="password"
              className="form-input"
              value={binanceApiSecret}
              onChange={(e) => setBinanceApiSecret(e.target.value)}
              placeholder="Enter your Binance API secret"
            />
          </div>
        </div>

        <div className="exchange-section trading-section">
          <h4 className="exchange-title">
            <div className="exchange-icon trading">⚙️</div>
            Trading Settings
          </h4>
          <div className="form-group">
            <label htmlFor="side-min-amt" className="form-label">Side Minimal Amount</label>
            <input
              id="side-min-amt"
              type="number"
              className="form-input"
              value={sideMinAmt}
              onChange={(e) => setSideMinAmt(e.target.value)}
              placeholder="Enter side minimal amount (integer)"
            />
          </div>
          <div className="form-group">
            <label htmlFor="max-amt" className="form-label">Maximum Trades Amount</label>
            <input
              id="max-amt"
              type="number"
              className="form-input"
              value={maxAmt}
              onChange={(e) => setMaxAmt(e.target.value)}
              placeholder="Enter maximum trades amount (integer)"
            />
          </div>
          <div className="form-group">
            <label htmlFor="min-price" className="form-label">Minimal Price</label>
            <input
              id="min-price"
              type="number"
              step="0.000001"
              className="form-input"
              value={minPrice}
              onChange={(e) => setMinPrice(e.target.value)}
              placeholder="Enter minimal price (decimal)"
            />
          </div>
          <div className="form-group">
            <label htmlFor="max-price" className="form-label">Maximum Price</label>
            <input
              id="max-price"
              type="number"
              step="0.000001"
              className="form-input"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder="Enter maximum price (decimal)"
            />
          </div>
        </div>

        <button className="save-button" onClick={saveApiKeys} disabled={loading}>
          {loading ? 'Saving...' : 'Save Settings'}
        </button>

        {message && (
          messageType === 'success' ? (
            <div className="success-message">{message}</div>
          ) : (
            <div className="error-message">{message}</div>
          )
        )}
      </div>
    </div>
  );
};

export default Settings;

